import {
  IndexedFilters,
  IndexedNestedFilter,
  IndexedFilterValue,
} from '../types/filters';
import { cloneDeep } from 'lodash-es';
import { FilterLabelLookup, OTHERS_OPTIONS } from './parse-indexed-filter-for-label';
import isNumberArray from '@/common/utils/is-number-array';
import { isTypeNumberRange } from './filter-menu-type-guards';
/**
 * Type guard for nested filter structures
 */
export function isIndexedNestedFilter(
  value: IndexedFilterValue
): value is IndexedNestedFilter {
  return typeof value === 'object' && !Array.isArray(value);
}

/**
 * Removes specified filter from selected filters state
 * @param filter filter to lookup and remove
 * @param selectedFilters current selected filters
 * @param shouldRemoveRange Should remove range if filter is an array if numbers
 * @param shouldKeepEmptyNestedFilters Should keep empty nested filters
 * @returns
 */
export default function removeSelectedFilter(
  filter: FilterLabelLookup,
  selectedFilters: IndexedFilters,
  shouldRemoveRange?: boolean,
  shouldKeepEmptyNestedFilters?: boolean
): IndexedFilters {
  const updatedFilters = cloneDeep(selectedFilters);
  const currentValue = updatedFilters[filter.key];

  if (Array.isArray(currentValue)) {
    if (
      isNumberArray(currentValue) &&
      currentValue.includes(Number(filter.value)) &&
      shouldRemoveRange
    ) {
      delete updatedFilters[filter.key];
      return updatedFilters;
    }

    // Handle removing the bucket of values for the "Others" option
    if (Array.isArray(filter.value)) {
      const filteredArray = currentValue.filter(
        (item) => !OTHERS_OPTIONS[filter.key].predicate(item)
      );

      if (filteredArray.length === 0) {
        delete updatedFilters[filter.key];
      } else {
        updatedFilters[filter.key] = filteredArray;
      }
      return updatedFilters;
    }

    const filteredArray = currentValue.filter((item) => item !== filter.value);
    if (filteredArray.length === 0) {
      delete updatedFilters[filter.key];
    } else {
      updatedFilters[filter.key] = filteredArray;
    }
    return updatedFilters;
  }

  if (isTypeNumberRange(currentValue)) {
    delete updatedFilters[filter.key];
    return updatedFilters;
  }

  if (isIndexedNestedFilter(currentValue) && typeof filter.value !== 'object') {
    if (!Array.isArray(filter.value) && filter.value in currentValue) {
      delete currentValue[filter.value as string];
      if (Object.keys(currentValue).length === 0) {
        delete updatedFilters[filter.key];
      }
      return updatedFilters;
    }

    for (const mainFilter in currentValue) {
      const subFilters = currentValue[mainFilter];

      // Note: We an assumption here that nested filters will not have an "Others" option so filter.value will always be just a FilterPrimitive.
      const filteredSubFilters = subFilters.filter((item) => item !== filter.value);

      if (filteredSubFilters.length === 0 && !shouldKeepEmptyNestedFilters) {
        delete currentValue[mainFilter];
      } else {
        currentValue[mainFilter] = filteredSubFilters;
      }
    }

    if (Object.keys(currentValue).length === 0) {
      delete updatedFilters[filter.key];
    }
  }

  return updatedFilters;
}
